import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNewDeviceFields } from 'modules/intelligent-layer/hooks';
import { BasicFieldsForm } from 'modules/intelligent-layer/forms/IntelligenceFieldsForm';
import { useTranslation } from 'utils/translation';
import { useModal } from '../../../../components/v2/Modal/useModal';
import { useModalContent } from '../../../../components/v2/Modal/useModalContent';
import Spacer from '../../../../components/v2/Spacer/Spacer';
import { mapValuesToPayload } from '../../../../modules/intelligent-layer/utils';
import { useCreateNewDevice } from '../hooks';
import Typography from 'components/v2/Typography/Typography';
import Section from 'components/v2/Section/Section';
import Loader from 'components/v2/Loader';
import Button, { ButtonGroup } from 'components/v2/Button/Button';
import Stack from '@mui/material/Stack/Stack';
import { applyFilters } from '../../../../core';
import Filters from '../../../../core/filters';
import { useQuery } from '@tanstack/react-query';
import { refetchActiveQueries } from '../../../../utils/QueryClientProvider';
import http from '../../../../utils/http';
import AccountQuotaGuard from '../../../../modules/users/components/AccountQuotaGuard';

const defaultRequiredFields = ['serial_number', 'device_type'];

const SuccessNotice = ({
  onCreateNext,
  onClose,
}: {
  onClose(): void;
  onCreateNext(): void;
}) => {
  const { t } = useTranslation();

  return (
    <Section>
      <Stack direction="column" alignItems="center">
        <Typography variant="h2">{t('Device has been created')}</Typography>
        <Spacer size={4} />
        <ButtonGroup>
          <Button onClick={onCreateNext}>{t('Create next device')}</Button>
          <Button onClick={onClose} variant="text">
            {t('Close')}
          </Button>
        </ButtonGroup>
      </Stack>
    </Section>
  );
};

type Props = {
  onClose(): void;
  onSuccess?(): void;
  defaultType?: { label: string; value: number } | null;
};

const NewDeviceForm = ({ onClose, onSuccess, defaultType = null }: Props) => {
  const { t } = useTranslation();
  const mutation = useCreateNewDevice();
  const [selectedType, setSelectedType] = useState<any | null>(defaultType);
  const modalContent = useModalContent();
  const formKey = selectedType?.value || 'unknown';
  const { data = [], isLoading } = useNewDeviceFields(selectedType);

  useEffect(() => {
    if (mutation.isError) {
      modalContent.scrollToTop();
    }
  }, [mutation.isError]);

  const defaultValuesQuery = useQuery({
    queryFn: async () => {
      let defaultValues = {};

      if (selectedType) {
        const id = selectedType.value;
        try {
          defaultValues = await http.get(`/api/device/default-values/${id}`);
        } catch (error) {
          console.error(error);
        }
      }

      const custom = await (async () =>
        applyFilters(
          Filters.device.newDeviceDefaultValues,
          {
            device_type: selectedType,
          },
          {
            deviceType: selectedType,
          },
        ))();

      return {
        ...defaultValues,
        ...custom,
      };
    },
    queryKey: ['new_device_form_default_values', formKey],
    refetchInterval: 0,
    gcTime: 0,
  });

  const requiredFields = applyFilters(
    Filters.device.newDeviceRequiredFields,
    defaultRequiredFields,
  );

  const fields = useMemo(() => {
    return data.map((item) => {
      if (requiredFields.includes(item.name)) {
        item.settings.required = true;
      }

      if (
        item.type === 'select' &&
        Number(item.settings.options?.length || 0) <= 1
      ) {
        item.settings.readOnly = true;
      }

      return item;
    });
  }, [data]);

  const defaultValuesFromFields = useMemo(() => {
    return fields.reduce(
      (prev, item) => {
        if (item.type === 'select' && item.settings.options?.length === 1) {
          prev[item.name] = item.settings.options[0];
        }

        return prev;
      },
      {} as Record<string, any>,
    );
  }, [fields]);

  const handleChange = useCallback((values: any) => {
    if (values.hasOwnProperty('device_type')) {
      setSelectedType(values['device_type']);
    }
  }, []);

  const onSubmit = async (values: any) => {
    const payload = mapValuesToPayload(values);

    try {
      await mutation.mutateAsync(payload);
      onSuccess?.();
      void refetchActiveQueries();
    } catch (error: any) {
      return Promise.reject({
        response: error?.response,
      });
    }
  };

  return (
    <>
      <Section>
        <Typography variant="h1">{t('Create new device')}</Typography>
      </Section>
      {isLoading || defaultValuesQuery.isLoading ? (
        <Section>
          <Loader />
        </Section>
      ) : mutation.isSuccess ? (
        <SuccessNotice onCreateNext={mutation.reset} onClose={onClose} />
      ) : (
        <BasicFieldsForm
          enableSuggestedValues
          key={formKey}
          defaultValues={{
            ...defaultValuesFromFields,
            ...defaultValuesQuery.data,
          }}
          onChange={handleChange}
          fields={fields}
          persister={onSubmit}
        />
      )}
    </>
  );
};

export const useCreateNewDeviceForm = () => {
  const modal = useModal();

  return (
    props: Omit<React.ComponentProps<typeof NewDeviceForm>, 'onClose'> = {},
  ) => {
    modal.open(
      ({ close }) => (
        <AccountQuotaGuard quota="maxAssets">
          <NewDeviceForm {...props} onClose={close} />
        </AccountQuotaGuard>
      ),
      {
        disableBackdropClose: true,
      },
    );
  };
};

export default NewDeviceForm;
